import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Grid,
  TextField,
  InputAdornment,
  Autocomplete,
  Button,
  Chip,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControlLabel,
  Switch,
  Badge,
  IconButton,
  Divider,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
import BrandingWatermarkIcon from '@mui/icons-material/BrandingWatermark';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import BusinessIcon from '@mui/icons-material/Business';
import CategoryIcon from '@mui/icons-material/Category';
import SortIcon from '@mui/icons-material/Sort';
import PercentIcon from '@mui/icons-material/Percent';
import NumbersIcon from '@mui/icons-material/Numbers';
import PlaceIcon from '@mui/icons-material/Place';
import PriceChangeIcon from '@mui/icons-material/PriceChange';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import ImageNotSupportedIcon from '@mui/icons-material/ImageNotSupported';
import SubdirectoryArrowRightIcon from '@mui/icons-material/SubdirectoryArrowRight';
import FilterListIcon from '@mui/icons-material/FilterList';
import type { Brand, Warehouse, Branch, InventoryCategory } from '../types/inventory.types';
import { useTranslation } from 'react-i18next';
import { AppDatePicker } from '../../../common/AppDatePicker';

const VAT_RATE_OPTIONS = [
  { value: 0, label: '%0' },
  { value: 1, label: '%1' },
  { value: 8, label: '%8' },
  { value: 10, label: '%10' },
  { value: 18, label: '%18' },
  { value: 20, label: '%20' },
];

const DEPARTMENT_NO_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 8].map((v) => ({ value: v, label: String(v) }));

const ECOMMERCE_OPTIONS: { value: 'all' | 'yes' | 'no'; labelKey: string }[] = [
  { value: 'all', labelKey: 'stock.filters.ecommerceAll' },
  { value: 'yes', labelKey: 'stock.filters.ecommerceYes' },
  { value: 'no', labelKey: 'stock.filters.ecommerceNo' },
];

export type InventoryFilterDraft = {
  category: InventoryCategory | null;
  brand: Brand | null;
  warehouse: Warehouse | null;
  branch: Branch | null;
  location: { id: string; code: string; name: string } | null;
  vatRate: number | null;
  departmentNo: number | null;
  minSalePrice: number | null;
  maxSalePrice: number | null;
  minQuantity: number | null;
  maxQuantity: number | null;
  createdFrom: string | null;
  createdTo: string | null;
  ecommerceFilter: 'all' | 'yes' | 'no';
  onlyWithoutImage: boolean;
  itemClasses: string[];
  productionMode: string | null;
};

export interface InventoryFiltersProps {
  open: boolean;
  onClose: () => void;
  /** Uygula: taslak filtreleri parent state'e yazar ve listeyi yeniler */
  onApply: (draft: InventoryFilterDraft) => void;

  hierarchicalCategories: InventoryCategory[];
  categoryHasChildren: (categoryId: string) => boolean;
  brands: Brand[];
  warehouses: Warehouse[];
  branches: Branch[];
  locationOptions?: { id: string; code: string; name: string }[];
  isSmartWarehouse?: boolean;

  /** Dialog açılırken taslağın başlangıç değeri (şu an uygulanan filtreler) */
  applied: InventoryFilterDraft;

  orderBy: string | null;
  onResetSort: () => void;

  /** Popup dışında gösterilen aktif chip'ler */
  activeFilters: { key: string; label: string; onDelete: () => void }[];
  onClearAllFilters: () => void;
  productionEnabled?: boolean;
}

type PanelKey = 'categoryBrand' | 'location' | 'tax' | 'priceStock' | 'date' | 'status' | 'production';

const emptyDraft = (): InventoryFilterDraft => ({
  category: null,
  brand: null,
  warehouse: null,
  branch: null,
  location: null,
  vatRate: null,
  departmentNo: null,
  minSalePrice: null,
  maxSalePrice: null,
  minQuantity: null,
  maxQuantity: null,
  createdFrom: null,
  createdTo: null,
  ecommerceFilter: 'all',
  onlyWithoutImage: false,
  itemClasses: [],
  productionMode: null,
});

export const InventoryFilters: React.FC<InventoryFiltersProps> = ({
  open,
  onClose,
  onApply,
  hierarchicalCategories,
  categoryHasChildren,
  brands,
  warehouses,
  branches,
  locationOptions,
  isSmartWarehouse,
  applied,
  orderBy,
  onResetSort,
  activeFilters,
  onClearAllFilters,
  productionEnabled = false,
}) => {
  const { t } = useTranslation('accounting');
  const [expanded, setExpanded] = useState<PanelKey | false>('categoryBrand');
  const [draft, setDraft] = useState<InventoryFilterDraft>(applied);

  // Dialog açıldığı anda uygulanan filtreleri taslağa kopyala (edit sırasında ezilmesin)
  useEffect(() => {
    if (open) {
      setDraft({ ...applied });
      setExpanded('categoryBrand');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const parseNum = (v: string): number | null => {
    if (v.trim() === '') return null;
    const n = Number(v.replace(',', '.'));
    return Number.isFinite(n) ? n : null;
  };

  const patch = <K extends keyof InventoryFilterDraft>(key: K, value: InventoryFilterDraft[K]) => {
    setDraft((prev) => ({ ...prev, [key]: value }));
  };

  const allCategoryOption = useMemo(
    () => ({ id: 'all', name: t('stock.filters.allCategories'), parentId: null, level: 0 } as InventoryCategory),
    [t]
  );

  // ─── Kaskad kategori filtresi ───────────────────────────────────
  // Kategoriler tek bir uzun listede değil, seviye seviye seçiliyor: bir kategori
  // seçilince altı varsa hemen altında yeni bir kutu açılıyor, o da seçilince bir
  // sonraki... Derinlik sabit değil, ağaç neyse o kadar iniyor.
  //
  // Taslakta yine TEK alan tutuluyor (`draft.category` = seçilen EN DERİN kategori);
  // yol bundan yukarı yürünerek türetiliyor. Böylece uygulama/temizleme/chip mantığı
  // ve backend sözleşmesi (`categoryId`) hiç değişmiyor — backend zaten seçilen
  // kategorinin altındaki tüm torunları kapsıyor.
  const childrenByParent = useMemo(() => {
    const knownIds = new Set(hierarchicalCategories.map((c) => c.id));
    const map = new Map<string | null, InventoryCategory[]>();

    hierarchicalCategories.forEach((c) => {
      // Üstü silinmiş kategoriler kök sayılır; aksi hâlde hiçbir seviyede görünmezlerdi.
      const parentKey = c.parentId && knownIds.has(c.parentId) ? c.parentId : null;
      const siblings = map.get(parentKey);
      if (siblings) siblings.push(c);
      else map.set(parentKey, [c]);
    });

    return map;
  }, [hierarchicalCategories]);

  /** Seçili kategoriden köke kadar olan zincir (kökten yaprağa sıralı). */
  const categoryPath = useMemo(() => {
    const byId = new Map(hierarchicalCategories.map((c) => [c.id, c]));
    const path: InventoryCategory[] = [];
    let current: InventoryCategory | null = draft.category;
    let guard = 0;

    while (current && guard < 10) {
      path.unshift(current);
      current = current.parentId ? byId.get(current.parentId) ?? null : null;
      guard += 1;
    }

    return path;
  }, [draft.category, hierarchicalCategories]);

  /** Ekranda çizilecek kutular: kökler + seçili her düğümün çocukları. */
  const categoryLevels = useMemo(() => {
    const levels: { options: InventoryCategory[]; value: InventoryCategory | null }[] = [];

    const withValue = (options: InventoryCategory[], value: InventoryCategory | null) =>
      value && !options.some((o) => o.id === value.id) ? [...options, value] : options;

    const roots = childrenByParent.get(null) ?? [];
    levels.push({ options: withValue(roots, categoryPath[0] ?? null), value: categoryPath[0] ?? null });

    categoryPath.forEach((node, index) => {
      const children = childrenByParent.get(node.id);
      if (!children || children.length === 0) return;
      const value = categoryPath[index + 1] ?? null;
      levels.push({ options: withValue(children, value), value });
    });

    return levels;
  }, [childrenByParent, categoryPath]);

  const panelCounts = useMemo(() => ({
    categoryBrand: (draft.category ? 1 : 0) + (draft.brand ? 1 : 0),
    location: (draft.warehouse ? 1 : 0) + (draft.branch ? 1 : 0) + (draft.location ? 1 : 0),
    tax: (draft.vatRate !== null ? 1 : 0) + (draft.departmentNo !== null ? 1 : 0),
    priceStock:
      (draft.minSalePrice !== null ? 1 : 0) +
      (draft.maxSalePrice !== null ? 1 : 0) +
      (draft.minQuantity !== null ? 1 : 0) +
      (draft.maxQuantity !== null ? 1 : 0),
    date: (draft.createdFrom ? 1 : 0) + (draft.createdTo ? 1 : 0),
    status: (draft.ecommerceFilter !== 'all' ? 1 : 0) + (draft.onlyWithoutImage ? 1 : 0) + (orderBy ? 1 : 0),
    production: draft.itemClasses.length + (draft.productionMode ? 1 : 0),
  }), [draft, orderBy]);

  const draftChips = useMemo(() => {
    const chips: { key: string; label: string; clear: () => void }[] = [];
    if (draft.category) chips.push({ key: 'cat', label: `Kategori: ${draft.category.name}`, clear: () => patch('category', null) });
    if (draft.brand) chips.push({ key: 'brand', label: `Marka: ${draft.brand.name}`, clear: () => patch('brand', null) });
    if (draft.warehouse) chips.push({ key: 'wh', label: `Depo: ${draft.warehouse.name}`, clear: () => patch('warehouse', null) });
    if (draft.branch) chips.push({ key: 'branch', label: `Şube: ${draft.branch.name}`, clear: () => patch('branch', null) });
    if (draft.location) chips.push({ key: 'loc', label: `Lokasyon: ${draft.location.code}`, clear: () => patch('location', null) });
    if (draft.vatRate !== null) chips.push({ key: 'vat', label: `KDV: %${draft.vatRate}`, clear: () => patch('vatRate', null) });
    if (draft.departmentNo !== null) chips.push({ key: 'dept', label: `ÖKC: ${draft.departmentNo}`, clear: () => patch('departmentNo', null) });
    if (draft.ecommerceFilter !== 'all') {
      chips.push({
        key: 'ecom',
        label: `E-Ticaret: ${draft.ecommerceFilter === 'yes' ? 'Açık' : 'Kapalı'}`,
        clear: () => patch('ecommerceFilter', 'all'),
      });
    }
    if (draft.onlyWithoutImage) chips.push({ key: 'noImg', label: t('stock.filters.onlyWithoutImage'), clear: () => patch('onlyWithoutImage', false) });
    if (draft.itemClasses.length > 0) chips.push({
      key: 'cls',
      label: `${t('stockCard.itemClass', { ns: 'production' })}: ${draft.itemClasses.map(value => value === 'NONE' ? t('stockCard.filters.none', { ns: 'production' }) : t(`stockCard.classes.${value}`, { ns: 'production' })).join(', ')}`,
      clear: () => patch('itemClasses', []),
    });
    if (draft.productionMode) chips.push({
      key: 'pm',
      label: `${t('stockCard.productionMode', { ns: 'production' })}: ${draft.productionMode === 'MAKE_TO_STOCK' ? t('stockCard.list.stocked', { ns: 'production' }) : t('stockCard.list.onSale', { ns: 'production' })}`,
      clear: () => patch('productionMode', null),
    });
    if (draft.minSalePrice !== null) chips.push({ key: 'minP', label: `Min fiyat: ${draft.minSalePrice}`, clear: () => patch('minSalePrice', null) });
    if (draft.maxSalePrice !== null) chips.push({ key: 'maxP', label: `Max fiyat: ${draft.maxSalePrice}`, clear: () => patch('maxSalePrice', null) });
    if (draft.minQuantity !== null) chips.push({ key: 'minQ', label: `Min stok: ${draft.minQuantity}`, clear: () => patch('minQuantity', null) });
    if (draft.maxQuantity !== null) chips.push({ key: 'maxQ', label: `Max stok: ${draft.maxQuantity}`, clear: () => patch('maxQuantity', null) });
    if (draft.createdFrom) chips.push({ key: 'cFrom', label: `Başlangıç: ${draft.createdFrom}`, clear: () => patch('createdFrom', null) });
    if (draft.createdTo) chips.push({ key: 'cTo', label: `Bitiş: ${draft.createdTo}`, clear: () => patch('createdTo', null) });
    return chips;
  }, [draft, t]);

  const handleAccordion =
    (panel: PanelKey) => (_: React.SyntheticEvent, isExpanded: boolean) => {
      setExpanded(isExpanded ? panel : false);
    };

  const handleApply = () => {
    onApply({ ...draft });
    onClose();
  };

  const handleClearDraft = () => setDraft(emptyDraft());

  const accordionSx = {
    border: '1px solid',
    borderColor: 'divider',
    borderRadius: '8px !important',
    mb: 1,
    '&:before': { display: 'none' },
    boxShadow: 'none',
  };

  const PanelTitle = ({ title, count }: { title: string; count: number }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
      <Typography variant="subtitle2" fontWeight={600} sx={{ flex: 1 }}>
        {title}
      </Typography>
      {count > 0 && (
        <Chip size="small" color="primary" label={count} sx={{ height: 22, minWidth: 22 }} />
      )}
    </Box>
  );

  // Autocomplete menüsü Dialog dışında portal edilmesin — seçim kaybını önler
  const acSlotProps = { popper: { disablePortal: true } };

  const warehouseOptions = useMemo(
    () => (draft.branch
      ? warehouses.filter((w) => String(w.branchesId ?? '') === String(draft.branch!.id))
      : warehouses),
    [warehouses, draft.branch]
  );

  // Lokasyon paneli taslak depo üzerinden (henüz uygulanmamış seçim de görünür)
  const draftIsSmartWarehouse = draft.warehouse?.warehouseType === 'Smart' || !!isSmartWarehouse;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      scroll="paper"
      disableRestoreFocus
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: { xs: '92vh', sm: '85vh' },
        },
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 1, pb: 1 }}>
        <FilterListIcon color="primary" />
        <Typography variant="h6" component="span" sx={{ flex: 1, fontWeight: 700 }}>
          {t('stock.filters.advancedSearchTitle')}
        </Typography>
        <IconButton onClick={onClose} size="small" aria-label="close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ pt: 1.5 }}>
        {(draftChips.length > 0 || activeFilters.length > 0) && (
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.75, mb: 1 }}>
              {draftChips.map((f) => (
                <Chip
                  key={f.key}
                  label={f.label}
                  size="small"
                  onDelete={f.clear}
                  color="primary"
                  variant="outlined"
                />
              ))}
            </Box>
            <Button
              size="small"
              color="error"
              variant="text"
              startIcon={<ClearAllIcon />}
              onClick={handleClearDraft}
            >
              {t('stock.filters.clearAll')}
            </Button>
            <Divider sx={{ mt: 1.5 }} />
          </Box>
        )}

        <Accordion expanded={expanded === 'categoryBrand'} onChange={handleAccordion('categoryBrand')} sx={accordionSx}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <PanelTitle title={t('stock.filters.panelCategoryBrand')} count={panelCounts.categoryBrand} />
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={1.5}>
              {categoryLevels.map((level, levelIndex) => {
                const isRootLevel = levelIndex === 0;
                const parentOfLevel = isRootLevel ? null : categoryPath[levelIndex - 1];
                const emptyOption = isRootLevel
                  ? allCategoryOption
                  : ({
                      id: 'all',
                      name: t('stock.filters.allSubcategories', { defaultValue: 'Tümü' }),
                      parentId: null,
                      level: 0,
                    } as InventoryCategory);

                return (
                  <Grid item xs={12} key={parentOfLevel ? `sub-${parentOfLevel.id}` : 'root'}>
                    <Autocomplete
                      fullWidth
                      size="small"
                      options={[emptyOption, ...level.options]}
                      getOptionLabel={(option) => option.name}
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                      value={level.value || emptyOption}
                      onChange={(_, newValue) => {
                        // Bir seviye boşaltılırsa üstündeki seçim geçerli filtre olur;
                        // kök seviye boşaltılırsa kategori filtresi tamamen kalkar.
                        if (!newValue || newValue.id === 'all') {
                          patch('category', parentOfLevel ?? null);
                        } else {
                          patch('category', newValue as InventoryCategory);
                        }
                      }}
                      slotProps={acSlotProps}
                      renderOption={(props, option) => {
                        const { key, ...optionProps } = props as any;
                        const hasChildren = option.id !== 'all' && categoryHasChildren(option.id);
                        return (
                          <li key={option.id} {...optionProps}>
                            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                              {option.id !== 'all' && (
                                <CategoryIcon sx={{ fontSize: 15, mr: 0.5, color: 'primary.main', opacity: 0.7 }} />
                              )}
                              <Typography variant="body2" sx={{ fontWeight: option.id === 'all' ? 400 : 600, flex: 1 }} noWrap>
                                {option.name}
                              </Typography>
                              {hasChildren && (
                                <Typography variant="caption" color="text.disabled" sx={{ ml: 1 }}>+alt</Typography>
                              )}
                            </Box>
                          </li>
                        );
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={
                            isRootLevel
                              ? t('stock.filters.category')
                              : `${t('stock.filters.subcategory', { defaultValue: 'Alt Kategori' })} — ${parentOfLevel?.name ?? ''}`
                          }
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                              <>
                                <InputAdornment position="start">
                                  {isRootLevel ? (
                                    <CategoryIcon sx={{ fontSize: 16 }} />
                                  ) : (
                                    <SubdirectoryArrowRightIcon sx={{ fontSize: 16 }} />
                                  )}
                                </InputAdornment>
                                {params.InputProps.startAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                      ListboxProps={{ style: { maxHeight: 240 } }}
                    />
                  </Grid>
                );
              })}
              <Grid item xs={12}>
                <Autocomplete
                  fullWidth
                  size="small"
                  options={brands}
                  getOptionLabel={(option) => option.name}
                  isOptionEqualToValue={(a, b) => a.id === b.id}
                  value={draft.brand}
                  onChange={(_, newValue) => patch('brand', newValue)}
                  slotProps={acSlotProps}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('stock.filters.brand')}
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <InputAdornment position="start"><BrandingWatermarkIcon sx={{ fontSize: 16 }} /></InputAdornment>
                            {params.InputProps.startAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        <Accordion expanded={expanded === 'location'} onChange={handleAccordion('location')} sx={accordionSx}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <PanelTitle title={t('stock.filters.panelLocation')} count={panelCounts.location} />
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={1.5}>
              <Grid item xs={12}>
                <Autocomplete
                  fullWidth
                  size="small"
                  options={warehouseOptions}
                  getOptionLabel={(option) => option.name}
                  isOptionEqualToValue={(a, b) => a.id === b.id}
                  value={draft.warehouse}
                  onChange={(_, newValue) => {
                    patch('warehouse', newValue);
                    // Depo değişince lokasyon geçersiz kalır
                    patch('location', null);
                  }}
                  slotProps={acSlotProps}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('stock.filters.warehouse')}
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <InputAdornment position="start"><WarehouseIcon sx={{ fontSize: 16 }} /></InputAdornment>
                            {params.InputProps.startAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>
              {draftIsSmartWarehouse && locationOptions && locationOptions.length > 0 && (
                <Grid item xs={12}>
                  <Autocomplete
                    fullWidth
                    size="small"
                    options={locationOptions}
                    getOptionLabel={(option) => `${option.code} - ${option.name}`}
                    isOptionEqualToValue={(a, b) => a.id === b.id}
                    value={draft.location}
                    onChange={(_, newValue) => patch('location', newValue)}
                    slotProps={acSlotProps}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t('stock.filters.location')}
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <>
                              <InputAdornment position="start"><PlaceIcon sx={{ fontSize: 16 }} /></InputAdornment>
                              {params.InputProps.startAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                </Grid>
              )}
              <Grid item xs={12}>
                <Autocomplete
                  fullWidth
                  size="small"
                  options={branches}
                  getOptionLabel={(option) => option.name}
                  isOptionEqualToValue={(a, b) => String(a.id) === String(b.id)}
                  value={draft.branch}
                  onChange={(_, newValue) => {
                    patch('branch', newValue);
                    // Seçili depo yeni şubeye ait değilse geçersiz kalır
                    if (newValue && draft.warehouse && String(draft.warehouse.branchesId ?? '') !== String(newValue.id)) {
                      patch('warehouse', null);
                      patch('location', null);
                    }
                  }}
                  slotProps={acSlotProps}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('stock.filters.branch')}
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <InputAdornment position="start"><BusinessIcon sx={{ fontSize: 16 }} /></InputAdornment>
                            {params.InputProps.startAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        <Accordion expanded={expanded === 'tax'} onChange={handleAccordion('tax')} sx={accordionSx}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <PanelTitle title={t('stock.filters.panelTax')} count={panelCounts.tax} />
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  fullWidth
                  size="small"
                  options={VAT_RATE_OPTIONS}
                  getOptionLabel={(option) => option.label}
                  value={VAT_RATE_OPTIONS.find((opt) => opt.value === draft.vatRate) || null}
                  onChange={(_, newValue) => patch('vatRate', newValue ? newValue.value : null)}
                  isOptionEqualToValue={(option, value) => option.value === value.value}
                  slotProps={acSlotProps}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('stock.filters.vat')}
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <InputAdornment position="start"><PercentIcon sx={{ fontSize: 16 }} /></InputAdornment>
                            {params.InputProps.startAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  fullWidth
                  size="small"
                  options={DEPARTMENT_NO_OPTIONS}
                  getOptionLabel={(option) => option.label}
                  value={DEPARTMENT_NO_OPTIONS.find((opt) => opt.value === draft.departmentNo) || null}
                  onChange={(_, newValue) => patch('departmentNo', newValue ? newValue.value : null)}
                  isOptionEqualToValue={(option, value) => option.value === value.value}
                  slotProps={acSlotProps}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('stock.filters.departmentNo')}
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <InputAdornment position="start"><NumbersIcon sx={{ fontSize: 16 }} /></InputAdornment>
                            {params.InputProps.startAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        <Accordion expanded={expanded === 'priceStock'} onChange={handleAccordion('priceStock')} sx={accordionSx}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <PanelTitle title={t('stock.filters.panelPriceStock')} count={panelCounts.priceStock} />
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={1.5}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label={t('stock.filters.minPrice')}
                  value={draft.minSalePrice ?? ''}
                  onChange={(e) => patch('minSalePrice', parseNum(e.target.value))}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start"><PriceChangeIcon sx={{ fontSize: 16 }} /></InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label={t('stock.filters.maxPrice')}
                  value={draft.maxSalePrice ?? ''}
                  onChange={(e) => patch('maxSalePrice', parseNum(e.target.value))}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start"><PriceChangeIcon sx={{ fontSize: 16 }} /></InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label={t('stock.filters.minQuantity')}
                  value={draft.minQuantity ?? ''}
                  onChange={(e) => patch('minQuantity', parseNum(e.target.value))}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start"><Inventory2Icon sx={{ fontSize: 16 }} /></InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label={t('stock.filters.maxQuantity')}
                  value={draft.maxQuantity ?? ''}
                  onChange={(e) => patch('maxQuantity', parseNum(e.target.value))}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start"><Inventory2Icon sx={{ fontSize: 16 }} /></InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        <Accordion expanded={expanded === 'date'} onChange={handleAccordion('date')} sx={accordionSx}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <PanelTitle title={t('stock.filters.panelDate')} count={panelCounts.date} />
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={6}>
                <AppDatePicker
                  fullWidth
                  size="small"
                  label={t('stock.filters.createdFrom')}
                  value={draft.createdFrom ?? ''}
                  onChange={(value) => patch('createdFrom', value || null)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <AppDatePicker
                  fullWidth
                  size="small"
                  label={t('stock.filters.createdTo')}
                  value={draft.createdTo ?? ''}
                  onChange={(value) => patch('createdTo', value || null)}
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        <Accordion expanded={expanded === 'status'} onChange={handleAccordion('status')} sx={accordionSx}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <PanelTitle title={t('stock.filters.panelStatus')} count={panelCounts.status} />
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={1.5}>
              <Grid item xs={12}>
                <Autocomplete
                  fullWidth
                  size="small"
                  disableClearable
                  options={ECOMMERCE_OPTIONS}
                  getOptionLabel={(option) => t(option.labelKey)}
                  value={ECOMMERCE_OPTIONS.find((o) => o.value === draft.ecommerceFilter) || ECOMMERCE_OPTIONS[0]}
                  onChange={(_, newValue) => patch('ecommerceFilter', newValue ? newValue.value : 'all')}
                  isOptionEqualToValue={(option, value) => option.value === value.value}
                  slotProps={acSlotProps}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('stock.filters.ecommerce')}
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <InputAdornment position="start"><ShoppingCartIcon sx={{ fontSize: 16 }} /></InputAdornment>
                            {params.InputProps.startAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={draft.onlyWithoutImage}
                      onChange={(e) => patch('onlyWithoutImage', e.target.checked)}
                      color="primary"
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <ImageNotSupportedIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                      <Typography variant="body2">{t('stock.filters.onlyWithoutImage')}</Typography>
                    </Box>
                  }
                />
              </Grid>
              {orderBy && (
                <Grid item xs={12}>
                  <Button size="small" variant="outlined" onClick={onResetSort} startIcon={<SortIcon />}>
                    {t('stock.filters.resetSort')}
                  </Button>
                </Grid>
              )}
            </Grid>
          </AccordionDetails>
        </Accordion>

        {productionEnabled && (
          <Accordion expanded={expanded === 'production'} onChange={handleAccordion('production')} sx={accordionSx}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <PanelTitle title={t('title', { ns: 'production' })} count={panelCounts.production} />
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={7}>
                  <Autocomplete
                    multiple
                    fullWidth
                    size="small"
                    options={['NONE', 'RAW', 'SEMI', 'FINISHED', 'PACKAGING', 'BYPRODUCT', 'TRADE']}
                    value={draft.itemClasses}
                    onChange={(_, value) => patch('itemClasses', value)}
                    getOptionLabel={(option) => option === 'NONE'
                      ? t('stockCard.filters.none', { ns: 'production' })
                      : t(`stockCard.classes.${option}`, { ns: 'production' })}
                    renderInput={(params) => (
                      <TextField {...params} label={t('stockCard.itemClass', { ns: 'production' })} />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={5}>
                  <Autocomplete
                    fullWidth
                    size="small"
                    options={['SALE_EXPLODE', 'MAKE_TO_STOCK']}
                    value={draft.productionMode}
                    onChange={(_, value) => patch('productionMode', value)}
                    getOptionLabel={(option) => option === 'MAKE_TO_STOCK'
                      ? t('stockCard.list.stocked', { ns: 'production' })
                      : t('stockCard.list.onSale', { ns: 'production' })}
                    renderInput={(params) => (
                      <TextField {...params} label={t('stockCard.productionMode', { ns: 'production' })} />
                    )}
                  />
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2.5, py: 1.5, gap: 1 }}>
        <Button color="inherit" variant="text" onClick={onClose}>
          {t('stock.filters.cancel', { defaultValue: 'İptal' })}
        </Button>
        <Button
          color="error"
          variant="text"
          startIcon={<ClearAllIcon />}
          onClick={handleClearDraft}
        >
          {t('stock.filters.clearAll')}
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button variant="contained" onClick={handleApply} startIcon={<FilterListIcon />}>
          {t('stock.filters.applyClose')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const InventoryFilterBadge: React.FC<{ count: number; children: React.ReactNode }> = ({
  count,
  children,
}) => (
  <Badge badgeContent={count > 0 ? count : 0} color="primary" invisible={count === 0}>
    {children}
  </Badge>
);
